import React, { useState, useEffect, createContext, useContext } from "react";
import "./theme.css";
import "./App.css";
import CAD from "./pages/CAD/CAD";
import Guide from "./pages/Main/Guide";
import ProjectDashboard from "./components/ProjectDashboard/ProjectDashboard";
import LoginForm from "./components/Auth/LoginForm";
import Home from "./pages/Main/Home";
import LoadingScreen from "./components/ProjectDashboard/LoadingScreen";
import NotificationModal from "./components/NotificationModal/NotificationModal";
import Onboarding from "./pages/Onboarding/Onboarding";
import OnboardingUseCase from "./pages/Onboarding/OnboardingUseCase";
import OnboardingRole from "./pages/Onboarding/OnboardingRole";
import OnboardingCredits from "./pages/Onboarding/OnboardingCredits";
import { AuthProvider, useAuth } from "./components/Auth/AuthProvider";
import { db } from "./firebase/config";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import Discord from "./pages/Discord/Discord";
import backIconLight from "./assets/send_light.svg";
import backIconDark from "./assets/send_dark.svg";
import diffusionIconLight from "./assets/diffusion_light.svg";
import diffusionIconDark from "./assets/diffusion_dark.svg";
import modelNotAvailableStyles from "./ModelNotAvailable.module.css";
import Terms from "./pages/Main/Terms";
import Privacy from "./pages/Main/Privacy";

type AppView =
  | "projects"
  | "cad"
  | "home"
  | "terms"
  | "privacy"
  | "guide"
  | "login"
  | "signup"
  | "onboarding"
  | "discord";
type DashboardView = "models" | "new-model" | "settings";
type Theme = "light" | "dark" | "system";
type ViewMode = "owner" | "viewer";

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType>({
  theme: "light",
  setTheme: () => {},
});

export const useTheme = () => useContext(ThemeContext);

const ModelNotAvailable: React.FC<{ reason: "not-found" | "error" }> = ({
  reason,
}) => {
  const { theme } = useTheme();
  const diffusionIcon =
    theme === "dark" ? diffusionIconDark : diffusionIconLight;
  const backIcon = theme === "dark" ? backIconDark : backIconLight;

  return (
    <div className={modelNotAvailableStyles.container}>
      <button
        className={modelNotAvailableStyles.iconButton}
        onClick={() => (window.location.href = "/")}
      >
        <img
          src={diffusionIcon}
          alt="Diffusion"
          className={modelNotAvailableStyles.iconImage}
        />
      </button>

      <div className={modelNotAvailableStyles.content}>
        <h2 className={modelNotAvailableStyles.title}>
          {reason === "not-found"
            ? "Model not available"
            : "Model not available"}
        </h2>
        <p className={modelNotAvailableStyles.message}>
          {reason === "not-found"
            ? "This model is either private or has been deleted."
            : "This model is either private or has been deleted."}
        </p>
        <button
          onClick={() => (window.location.href = "/")}
          className={modelNotAvailableStyles.button}
        >
          Back to Noah
        </button>
      </div>
    </div>
  );
};

function AppContent() {
  const {
    user,
    userProfile,
    loading,
    needsEmailVerification,
    markNotificationAsSeen,
    updateUserProfile,
  } = useAuth();
  const { theme, setTheme } = useTheme();
  const [currentView, setCurrentView] = useState<AppView>("home");
  const [isLoadingSharedView, setIsLoadingSharedView] = useState(false);
  const [isToolbarExpanded, setIsToolbarExpanded] = useState(true);

  // ✅ ADD THIS DEBUG WRAPPER
  useEffect(() => {
    // console.log(
    //   "🔍 APP.TSX: isLoadingSharedView changed to:",
    //   isLoadingSharedView
    // );
    console.trace("Stack trace:");
  }, [isLoadingSharedView]);
  // NEW: Track current project ID (can be null for empty state)
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);

  const [showLoginForm, setShowLoginForm] = useState(false);
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);
  const [targetDashboardView, setTargetDashboardView] =
    useState<DashboardView>("new-model");
  const [viewMode, setViewMode] = useState<ViewMode>("owner");
  const [sharedProjectData, setSharedProjectData] = useState<any>(null);
  const [shareLoadError, setShareLoadError] = useState<string | null>(null);
  const [showNotification, setShowNotification] = useState(false);
  const [hasUnreadNotification, setHasUnreadNotification] = useState(false);
  const CURRENT_NOTIFICATION_VERSION = 1;

  // Add this new useEffect around line 600, after the other useEffects:
  useEffect(() => {
    // Clear shared view loading when switching away from shared view
    if (isLoadingSharedView && currentView !== "cad") {
      // console.log(
      //   "🔄 Clearing shared view loading - switched to different view"
      // );
      setIsLoadingSharedView(false);
    }
  }, [currentView, isLoadingSharedView]);

  // Clear shared view loading when active tab is not the shared model
  useEffect(() => {
    if (isLoadingSharedView && currentProjectId && sharedProjectData) {
      // If the current project is NOT the shared model, clear loading
      if (currentProjectId !== sharedProjectData.id) {
        // console.log(
        //   "🔄 Clearing shared view loading - active tab is not the shared model"
        // );
        setIsLoadingSharedView(false);
      }
    }
  }, [currentProjectId, sharedProjectData, isLoadingSharedView]);
  // Also clear when switching to a different project
  useEffect(() => {
    // Clear shared view loading when switching to a different project
    if (isLoadingSharedView && viewMode === "owner") {
      // console.log("🔄 Clearing shared view loading - switched to owner mode");
      setIsLoadingSharedView(false);
    }
  }, [viewMode, isLoadingSharedView]);

  useEffect(() => {
    if (
      userProfile?.themePreference &&
      !localStorage.getItem("user-theme-preference")
    ) {
      setTheme(userProfile.themePreference);
    }
  }, []);

  useEffect(() => {
    if (userProfile && userProfile.onboardingCompleted) {
      const lastSeen = userProfile.lastSeenNotificationVersion || 0;
      setHasUnreadNotification(lastSeen < CURRENT_NOTIFICATION_VERSION);
    }
  }, [userProfile]);

  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;

      window.scrollTo(0, 0);

      if (path === "/home") {
        setCurrentView("home");
      } else if (path === "/terms") {
        setCurrentView("terms");
      } else if (path === "/privacy") {
        setCurrentView("privacy");
      } else if (path === "/guide") {
        setCurrentView("guide");
      } else if (path === "/login") {
        setCurrentView("login");
      } else if (path === "/signup") {
        setCurrentView("signup");
      } else if (path === "/discord") {
        // ADD THIS
        setCurrentView("discord");
      } else if (
        path === "/onboarding" ||
        path === "/onboarding/use-case" ||
        path === "/onboarding/role" ||
        path === "/onboarding/credits"
      ) {
        setCurrentView("onboarding");
      } else if (
        path === "/new" ||
        path === "/models" ||
        path === "/settings"
      ) {
        setCurrentView("projects");
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const handleCloseNotification = async () => {
    setShowNotification(false);
    setHasUnreadNotification(false);
    await markNotificationAsSeen(CURRENT_NOTIFICATION_VERSION);
  };

  const handleOpenNotification = () => {
    setShowNotification(true);
  };

  // Dynamic page title based on current view
  useEffect(() => {
    const updateTitle = () => {
      switch (currentView) {
        case "home":
          document.title = "Noah - CAD, reimagined with AI";
          break;
        case "terms":
          document.title = "Terms of Service";
          break;
        case "privacy":
          document.title = "Privacy Policy";
          break;
        case "guide":
          document.title = "Getting Started with Noah (Beta)";
          break;
        case "discord":
          document.title = "Join the Noah community";
          break;
        case "cad":
        case "projects":
          document.title = "Noah";
          break;
        case "login":
          document.title = "Noah";
          break;
        case "signup":
          document.title = "Noah";
          break;
        case "onboarding":
          document.title = "Noah";
          break;
        default:
          document.title = "Noah";
      }
    };

    updateTitle();
  }, [currentView]);

  // Helper function to check if onboarding is incomplete
  const isOnboardingIncomplete = (profile: typeof userProfile): boolean => {
    if (!profile) return false;
    return (
      !profile.fullName ||
      !profile.useCase ||
      !profile.role ||
      !profile.onboardingCompleted
    );
  };

  // Helper function to get the correct onboarding path based on profile state
  const getOnboardingPath = (profile: typeof userProfile): string => {
    if (!profile?.fullName) {
      return "/onboarding";
    }
    if (!profile?.useCase) {
      return "/onboarding/use-case";
    }
    if (!profile?.role) {
      return "/onboarding/role";
    }
    return "/new"; // Onboarding complete
  };

  useEffect(() => {
    const checkForSharedProject = async () => {
      const path = window.location.pathname;
      const searchParams = new URLSearchParams(window.location.search);

      // HANDLE STATIC PAGES FIRST - before any auth/onboarding checks
      if (path === "/terms") {
        // console.log("🔍 Terms page detected");
        setCurrentView("terms");
        return;
      }

      if (path === "/privacy") {
        // console.log("🔍 Privacy page detected");
        setCurrentView("privacy");
        return;
      }

      if (path === "/guide") {
        // console.log("🔍 Guide page detected");
        setCurrentView("guide");
        return;
      }

      if (path === "/home") {
        // console.log("🔍 Home page detected");
        setCurrentView("home");
        return;
      }

      // ADD THIS HERE ↓
      if (path === "/discord") {
        setCurrentView("discord");
        return;
      }

      if (path === "/login") {
        // console.log("🔍 Login page detected");
        setCurrentView("login");
        return;
      }

      if (path === "/signup") {
        // console.log("🔍 Signup page detected");
        setCurrentView("signup");
        return;
      }

      // CRITICAL: Handle onboarding redirects AFTER static pages
      if (user && !needsEmailVerification && userProfile) {
        // Check if onboarding is incomplete
        if (isOnboardingIncomplete(userProfile)) {
          const correctPath = getOnboardingPath(userProfile);

          // Always redirect to correct onboarding step, regardless of current path
          // (except for static pages like terms/privacy)
          if (path !== "/terms" && path !== "/privacy" && path !== "/home") {
            if (path !== correctPath) {
              // console.log(
              //   `🔒 Onboarding incomplete - redirecting to ${correctPath}`
              // );
              window.history.replaceState({}, "", correctPath);
            }
            setCurrentView("onboarding");
            return;
          }
        }

        // CRITICAL: If onboarding IS complete, redirect away from onboarding pages
        if (!isOnboardingIncomplete(userProfile)) {
          if (
            path === "/onboarding" ||
            path === "/onboarding/use-case" ||
            path === "/onboarding/role" ||
            path === "/onboarding/credits"
          ) {
            // console.log("🔒 Onboarding already complete - redirecting to /new");
            window.history.replaceState({}, "", "/new");
            setCurrentView("projects");
            return;
          }
        }
      }

      // Skip all other logic if user hasn't completed onboarding
      if (user && userProfile && isOnboardingIncomplete(userProfile)) {
        // Allow static pages even during onboarding
        if (path === "/terms") {
          setCurrentView("terms");
          return;
        }
        if (path === "/privacy") {
          setCurrentView("privacy");
          return;
        }
        if (path === "/guide") {
          // console.log("🔍 Guide page detected");
          setCurrentView("guide");
          return;
        }
        if (path === "/home") {
          setCurrentView("home");
          return;
        }
        return;
      }

      // Handle static pages
      if (path === "/onboarding") {
        // console.log("🔍 Onboarding page detected");
        setCurrentView("onboarding");
        return;
      }

      if (path === "/onboarding/use-case") {
        // console.log("🔍 Onboarding use case page detected");
        setCurrentView("onboarding");
        return;
      }

      if (path === "/onboarding/role") {
        // console.log("🔍 Onboarding role page detected");
        setCurrentView("onboarding");
        return;
      }

      if (path === "/onboarding/credits") {
        // console.log("🔍 Onboarding credits page detected");
        setCurrentView("onboarding");
        return;
      }

      if (path === "/terms") {
        // console.log("🔍 Terms page detected");
        setCurrentView("terms");
        return;
      }

      if (path === "/privacy") {
        // console.log("🔍 Privacy page detected");
        setCurrentView("privacy");
        return;
      }

      if (path === "/home") {
        // console.log("🔍 Home page detected");
        setCurrentView("home");
        return;
      }

      // ADD THIS HERE ↓
      if (path === "/discord") {
        setCurrentView("discord");
        return;
      }

      if (path === "/login") {
        // console.log("🔍 Login page detected");
        setCurrentView("login");
        return;
      }

      if (path === "/signup") {
        // console.log("🔍 Signup page detected");
        setCurrentView("signup");
        return;
      }

      // Check if on root path and user is logged in
      if (path === "/" && user) {
        // Don't redirect to /new if onboarding is incomplete
        if (userProfile && isOnboardingIncomplete(userProfile)) {
          // Onboarding check above will handle the redirect
          return;
        }
        window.history.replaceState({}, "", "/new");
        setCurrentView("projects");
        return;
      }

      // Check if on dashboard pages (when user is logged in)
      if (path === "/new" || path === "/models" || path === "/settings") {
        // Don't allow access if onboarding incomplete - redirect will happen above
        if (userProfile && isOnboardingIncomplete(userProfile)) {
          return;
        }
        setCurrentView("projects");
        return;
      }

      // Check if viewing a specific project via /models/[id]
      if (path.startsWith("/models/")) {
        const projectIdFromUrl = path.split("/models/")[1];
        if (projectIdFromUrl && user) {
          try {
            const projectDoc = await getDoc(
              doc(db, "projects", projectIdFromUrl),
            );
            if (projectDoc.exists()) {
              const projectData = projectDoc.data();

              if (projectData.userId === user.uid) {
                setCurrentProjectId(projectIdFromUrl);
                setCurrentView("cad");
                setViewMode("owner");
              } else if (projectData.isPublic) {
                setSharedProjectData({ id: projectIdFromUrl, ...projectData });
                setCurrentProjectId(projectIdFromUrl);
                setViewMode("viewer");
                setCurrentView("cad");
                setShareLoadError(null);
              } else {
                setShareLoadError("not-found");
                setViewMode("viewer");
                setSharedProjectData(null);
              }
            } else {
              setShareLoadError("not-found");
              setViewMode("viewer");
              setSharedProjectData(null);
            }
          } catch (error) {
            // console.error("Error checking project ownership:", error);
            setShareLoadError("error");
            setViewMode("viewer");
            setSharedProjectData(null);
          }
          return;
        } else if (!user) {
          setCurrentView("login");
          window.history.pushState({}, "", "/login");
        }
        return;
      }

      // Check if returning from Stripe billing portal
      if (searchParams.has("billing_return")) {
        const returnUrl = sessionStorage.getItem("billing_return_url");

        // console.log("🔄 Billing return detected");
        // console.log("🔄 Stored return URL:", returnUrl);

        if (returnUrl) {
          sessionStorage.removeItem("billing_return_url");
          // console.log("🔄 Redirecting to:", returnUrl);
          window.location.href = returnUrl;
          return;
        }

        window.history.replaceState({}, "", "/settings");
        setCurrentView("projects");
        return;
      }

      // Check if returning from Stripe during onboarding
      if (
        searchParams.has("success") &&
        searchParams.get("type") === "credits"
      ) {
        const isOnboardingPurchase = sessionStorage.getItem(
          "onboarding_credit_purchase",
        );

        if (isOnboardingPurchase) {
          sessionStorage.removeItem("onboarding_credit_purchase");

          // Complete onboarding and redirect to /new
          if (user && userProfile) {
            await updateUserProfile({
              onboardingCompleted: true,
            });
          }

          window.history.replaceState({}, "", "/new");
          setCurrentView("projects");
          return;
        }
      }

      // Check if returning from Stripe
      if (searchParams.has("stripe_return")) {
        const status = searchParams.get("stripe_return");
        const returnUrl = sessionStorage.getItem("stripe_return_url");

        // console.log("🔄 Stripe return detected:", status);
        // console.log("🔄 Stored return URL:", returnUrl);

        if (returnUrl) {
          sessionStorage.removeItem("stripe_return_url");
          // console.log("🔄 Redirecting to:", returnUrl);
          window.location.href = returnUrl;
          return;
        }

        const returnProjectId = sessionStorage.getItem("stripe_return_project");
        if (returnProjectId) {
          // console.log("🔄 Fallback: Returning to project:", returnProjectId);

          const storedContext = sessionStorage.getItem("stripe_return_context");
          const storedSelection = sessionStorage.getItem(
            "stripe_return_selection",
          );

          if (storedContext || storedSelection) {
            try {
              const context: any = {};

              if (storedContext) {
                const parsedContext = JSON.parse(storedContext);
                context.input = parsedContext.input;
              }

              if (storedSelection) {
                context.selection = JSON.parse(storedSelection);
              }

              (window as any).__restoreProjectContext = context;
            } catch (error) {
              // console.error("Failed to parse stored context:", error);
            }
          }

          window.history.replaceState({}, "", "/");
          sessionStorage.removeItem("stripe_return_context");
          sessionStorage.removeItem("stripe_return_selection");
          if (status === "canceled") {
            sessionStorage.removeItem("pendingCommand");
          }

          setCurrentProjectId(returnProjectId);
          setCurrentView("cad");
          setViewMode("owner");
          return;
        }

        window.history.replaceState({}, "", "/new");
        setCurrentView("projects");
        return;
      }

      // Check if canceled Stripe checkout
      if (searchParams.has("canceled")) {
        const returnProjectId = sessionStorage.getItem("stripe_return_project");
        if (returnProjectId) {
          // console.log("🔄 Returning to project after cancel:", returnProjectId);

          const storedContext = sessionStorage.getItem("stripe_return_context");
          const storedSelection = sessionStorage.getItem(
            "stripe_return_selection",
          );

          if (storedContext || storedSelection) {
            try {
              const context: any = {};

              if (storedContext) {
                const parsedContext = JSON.parse(storedContext);
                context.input = parsedContext.input;
              }

              if (storedSelection) {
                context.selection = JSON.parse(storedSelection);
              }

              (window as any).__restoreProjectContext = context;
            } catch (error) {
              // console.error("Failed to parse stored context:", error);
            }
          }

          sessionStorage.removeItem("pendingCommand");
          window.history.replaceState({}, "", "/");
          sessionStorage.removeItem("stripe_return_context");
          sessionStorage.removeItem("stripe_return_selection");

          setCurrentProjectId(returnProjectId);
          setCurrentView("cad");
          setViewMode("owner");
          return;
        }
      }

      // Check for shared project via /share/ URL
      if (path.startsWith("/share/")) {
        const shareToken = path.split("/share/")[1];
        await loadSharedProject(shareToken);
        return;
      }
    };

    checkForSharedProject();
  }, [user, userProfile, needsEmailVerification, window.location.pathname]);

  // Prevent loading overlay from appearing when executing commands on non-shared tabs
  useEffect(() => {
    const handleBeforeCommand = () => {
      const currentPath = window.location.pathname;
      const isOnShareUrl = currentPath.startsWith("/share/");

      if (isOnShareUrl && currentProjectId) {
        const sharedProjectId = sessionStorage.getItem("sharedProjectId");

        // If we're executing a command on a tab that's NOT the shared model
        if (sharedProjectId && currentProjectId !== sharedProjectId) {
          // console.log(
          //   "🔒 Preventing loading overlay - command on non-shared tab"
          // );
          setIsLoadingSharedView(false);
        }
      }
    };

    // Listen for command execution
    window.addEventListener("commandExecuting", handleBeforeCommand);

    return () => {
      window.removeEventListener("commandExecuting", handleBeforeCommand);
    };
  }, [currentProjectId]);

  useEffect(() => {
    if (user && !needsEmailVerification && userProfile) {
      const path = window.location.pathname;

      // CRITICAL: Block all navigation if onboarding incomplete
      if (isOnboardingIncomplete(userProfile)) {
        // console.log("🔒 Onboarding incomplete - redirecting to onboarding");
        const correctPath = getOnboardingPath(userProfile);
        window.history.replaceState({}, "", correctPath);
        setCurrentView("onboarding");
        return;
      }

      // CRITICAL: Never touch /share/ URLs - they must stay as viewer mode
      if (path.startsWith("/share/")) {
        // console.log("🔒 Share URL detected - skipping owner mode logic");
        return;
      }

      // CRITICAL: Never touch static pages - they must stay as their view
      if (
        path === "/terms" ||
        path === "/privacy" ||
        path === "/home" ||
        path === "/guide"
      ) {
        // console.log("🔒 Static page detected - skipping dashboard logic");
        return;
      }

      // CRITICAL: Never touch onboarding page
      if (
        path === "/onboarding" ||
        path === "/onboarding/use-case" ||
        path === "/onboarding/role" ||
        path === "/onboarding/credits"
      ) {
        // console.log("🔒 Onboarding page detected - skipping dashboard logic");
        return;
      }

      if (path === "/login" || path === "/signup") {
        window.history.replaceState({}, "", "/new");
        setCurrentView("projects");
        return;
      }

      if (path === "/new" || path === "/models" || path === "/settings") {
        setCurrentView("projects");
      }
    }
  }, [user, needsEmailVerification, userProfile]);

  // Around line 520, in the loadSharedProject function, add cleanup:
  const loadSharedProject = async (shareToken: string) => {
    setIsLoadingSharedView(true);

    try {
      const projectsRef = collection(db, "projects");
      const q = query(
        projectsRef,
        where("shareToken", "==", shareToken),
        where("isPublic", "==", true),
      );
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const projectData = snapshot.docs[0];
        const projectId = projectData.id;

        setSharedProjectData({ id: projectId, ...projectData.data() });
        setCurrentProjectId(projectId);
        setViewMode("viewer");
        setCurrentView("cad");
        setShareLoadError(null);

        window.history.replaceState({}, "", `/share/${shareToken}`);

        sessionStorage.setItem("isSharedView", "true");
        sessionStorage.setItem("shareToken", shareToken);
        sessionStorage.setItem("sharedProjectId", projectId); // ← ADD THIS LINE
      } else {
        setShareLoadError("not-found");
        setViewMode("viewer");
        setSharedProjectData(null);
        setIsLoadingSharedView(false);
      }
    } catch (error) {
      setShareLoadError("error");
      setViewMode("viewer");
      setSharedProjectData(null);
      setIsLoadingSharedView(false);
    }
  };

  const handleOpenProject = (projectId: string) => {
    setCurrentProjectId(projectId);
    setCurrentView("cad");
    setViewMode("owner");
    window.history.pushState({}, "", `/models/${projectId}`);
  };

  const handleBackToProjects = (targetView?: DashboardView) => {
    setCurrentProjectId(null);
    setCurrentView("projects");

    setViewMode("owner");
    setSharedProjectData(null);

    let newPath = "/new";
    let actualTargetView: DashboardView = "new-model";

    if (targetView === "models") {
      newPath = "/models";
      actualTargetView = "models";
    } else if (targetView === "settings") {
      newPath = "/settings";
      actualTargetView = "settings";
    }

    setTargetDashboardView(actualTargetView);
    window.history.pushState({}, "", newPath);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  // ADD THIS HERE ↓
  if (currentView === "discord") {
    return <Discord />;
  }
  // END OF ADDITION ↑

  if (user && needsEmailVerification) {
    return (
      <LoginForm
        initialMode={
          window.location.pathname === "/signup" ? "signup" : "login"
        }
      />
    );
  }

  if (
    user &&
    !needsEmailVerification &&
    window.location.pathname === "/" &&
    currentView !== "projects"
  ) {
    return <LoadingScreen />;
  }

  if (!user) {
    if (currentView === "login") {
      return <LoginForm initialMode="login" />;
    }

    if (currentView === "signup") {
      return <LoginForm initialMode="signup" />;
    }

    if (currentView === "terms") {
      return (
        <Terms
          onNavigateToHome={() => setCurrentView("home")}
          onNavigateToSignUp={() => {
            setCurrentView("signup");
            window.history.pushState({}, "", "/signup");
          }}
          onNavigateToLogin={() => {
            setCurrentView("login");
            window.history.pushState({}, "", "/login");
          }}
        />
      );
    }

    if (currentView === "privacy") {
      return (
        <Privacy
          onNavigateToHome={() => setCurrentView("home")}
          onNavigateToSignUp={() => {
            setCurrentView("signup");
            window.history.pushState({}, "", "/signup");
          }}
          onNavigateToLogin={() => {
            setCurrentView("login");
            window.history.pushState({}, "", "/login");
          }}
        />
      );
    }

    if (currentView === "guide") {
      return (
        <Guide
          onNavigateToHome={() => {
            setCurrentView("home");
            window.history.pushState({}, "", "/home");
          }}
          onNavigateToSignUp={() => {
            setCurrentView("signup");
            window.history.pushState({}, "", "/signup");
          }}
          onNavigateToLogin={() => {
            setCurrentView("login");
            window.history.pushState({}, "", "/login");
          }}
        />
      );
    }

    // Default to Home for non-authenticated users
    return (
      <Home
        onNavigateToSignUp={() => {
          setCurrentView("signup");
          window.history.pushState({}, "", "/signup");
        }}
        onNavigateToLogin={() => {
          setCurrentView("login");
          window.history.pushState({}, "", "/login");
        }}
      />
    );
  }

  // FIXED: Smart onboarding routing based on profile state, not just URL
  if (currentView === "onboarding") {
    // If they have everything except onboardingCompleted, show credits page
    // if (
    //   userProfile?.fullName &&
    //   userProfile?.useCase &&
    //   userProfile?.role &&
    //   !userProfile?.onboardingCompleted
    // ) {
    //   if (window.location.pathname !== "/onboarding/credits") {
    //     window.history.replaceState({}, "", "/onboarding/credits");
    //   }
    //   return <OnboardingCredits />;
    // }
    // If they have name and use case but no role, show role page
    if (userProfile?.fullName && userProfile?.useCase && !userProfile?.role) {
      if (window.location.pathname !== "/onboarding/role") {
        window.history.replaceState({}, "", "/onboarding/role");
      }
      return <OnboardingRole />;
    }
    // If they have a name but no use case, show use case page
    if (userProfile?.fullName && !userProfile?.useCase) {
      if (window.location.pathname !== "/onboarding/use-case") {
        window.history.replaceState({}, "", "/onboarding/use-case");
      }
      return <OnboardingUseCase />;
    }
    // If they don't have a name, show name page
    if (!userProfile?.fullName) {
      if (window.location.pathname !== "/onboarding") {
        window.history.replaceState({}, "", "/onboarding");
      }
      return <Onboarding />;
    }
    // Fallback based on URL (for edge cases)
    if (window.location.pathname === "/onboarding/credits") {
      return <OnboardingCredits />;
    }
    if (window.location.pathname === "/onboarding/role") {
      return <OnboardingRole />;
    }
    if (window.location.pathname === "/onboarding/use-case") {
      return <OnboardingUseCase />;
    }
    return <Onboarding />;
  }

  return (
    <div className="App">
      <NotificationModal
        isOpen={showNotification}
        onClose={handleCloseNotification}
      />

      {/* ADD THIS OVERLAY */}
      {/* Loading overlay - only show if NOT suppressed */}
      {isLoadingSharedView && !(window as any).__suppressSharedViewLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
          }}
        >
          <LoadingScreen />
        </div>
      )}

      {shareLoadError ? (
        <ModelNotAvailable reason={shareLoadError as "not-found" | "error"} />
      ) : currentView === "terms" ? (
        <Terms
          onNavigateToSignUp={() => {
            setCurrentView("signup");
            window.history.pushState({}, "", "/signup");
          }}
          onNavigateToLogin={() => {
            setCurrentView("login");
            window.history.pushState({}, "", "/login");
          }}
        />
      ) : currentView === "privacy" ? (
        <Privacy
          onNavigateToHome={() => {
            setCurrentView("home");
            window.history.pushState({}, "", "/home");
          }}
          onNavigateToSignUp={() => {
            setCurrentView("signup");
            window.history.pushState({}, "", "/signup");
          }}
          onNavigateToLogin={() => {
            setCurrentView("login");
            window.history.pushState({}, "", "/login");
          }}
        />
      ) : currentView === "guide" ? (
        <Guide
          onNavigateToHome={() => {
            setCurrentView("home");
            window.history.pushState({}, "", "/home");
          }}
          onNavigateToSignUp={() => {
            setCurrentView("signup");
            window.history.pushState({}, "", "/signup");
          }}
          onNavigateToLogin={() => {
            setCurrentView("login");
            window.history.pushState({}, "", "/login");
          }}
        />
      ) : currentView === "projects" ? (
        <CAD
          projectId={activeProjectId}
          onBackToProjects={handleBackToProjects}
          onOpenProject={handleOpenProject}
          viewMode="owner"
          isSharedView={false}
          onProjectCreated={(newId) => {
            // console.log(`📦 App.tsx received new project ID: ${newId}`);
            setActiveProjectId(newId);
            setCurrentProjectId(newId);
          }}
          onProjectCleared={() => {
            // console.log(`📦 App.tsx: All tabs closed, clearing projectIds`);
            setActiveProjectId(null);
            setCurrentProjectId(null); // ← CHANGED: Also clear currentProjectId
          }}
          onToolbarExpandChange={(expanded) => setIsToolbarExpanded(expanded)} // ← ADD THIS
        />
      ) : (
        <CAD
          projectId={currentProjectId}
          onBackToProjects={handleBackToProjects}
          onOpenProject={handleOpenProject}
          viewMode={viewMode}
          isSharedView={viewMode === "viewer"}
          initialProjectName={
            currentProjectId
              ? sessionStorage.getItem(`project_${currentProjectId}_name`) ||
                undefined
              : undefined
          }
          onProjectCreated={(newId) => {
            // console.log(`📦 App.tsx received new project ID: ${newId}`);
            setActiveProjectId(newId);
            setCurrentProjectId(newId);
          }}
          onProjectCleared={() => {
            // console.log(`📦 App.tsx: All tabs closed, clearing projectIds`);
            setActiveProjectId(null);
            setCurrentProjectId(null);
          }}
          onSharedModelLoaded={() => {
            // console.log(
            //   "🎉 App.tsx: Shared model loaded, hiding loading screen"
            // );
            setIsLoadingSharedView(false);
          }}
          onToolbarExpandChange={(expanded) => setIsToolbarExpanded(expanded)} // ← ADD THIS
        />
      )}
    </div>
  );
}

function App() {
  // REPLACE the entire useState hook for theme with this:
  const [theme, setThemeState] = useState<Theme>(() => {
    const savedTheme = localStorage.getItem("user-theme-preference");
    return (savedTheme as Theme) || "system"; // Changed default from "light" to "system"
  });

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem("user-theme-preference", newTheme);
  };

  useEffect(() => {
    const root = document.documentElement;

    const applyTheme = (actualTheme: "light" | "dark") => {
      if (actualTheme === "dark") {
        root.classList.add("dark-theme");
      } else {
        root.classList.remove("dark-theme");
      }

      // WINDOWS FIREFOX FIX: Force multiple repaints
      void root.offsetHeight;
      requestAnimationFrame(() => {
        void root.offsetHeight;
        requestAnimationFrame(() => {
          void root.offsetHeight;
        });
      });
    };

    if (theme === "system") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      applyTheme(mediaQuery.matches ? "dark" : "light");

      const handleChange = (e: MediaQueryListEvent) => {
        applyTheme(e.matches ? "dark" : "light");
      };

      mediaQuery.addEventListener("change", handleChange);

      return () => {
        mediaQuery.removeEventListener("change", handleChange);
      };
    } else {
      applyTheme(theme);
    }
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeContext.Provider>
  );
}

export default App;
